/**
 * Integrytas Group — Email Distribution System (EDS)
 * Page: EDS.TEMPLATE.LIST  —  Email Template Management
 *
 * Grid + right-side drawer with two tabs:
 *   Info          — internal_code, type, from_name, from_email, reply_to, active
 *   Content       — per-language subject / preheader / HTML body / plain-text tabs
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useLanguageOptions } from '../../lib/use_language_options';
import { formatDate } from '../../lib/format_date';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';
import type {
  EdsEmailTemplateRow,
  EdsEmailTemplateTlRow,
  EdsTemplateTypeRow,
  EdsTemplateSortKey,
  EdsTemplateTlForm,
} from '../../types/eds';

const PAGE_CODE = 'EDS.TEMPLATE.LIST';
registerUdcHost(PAGE_CODE, 'eds_email_template', 'Email Templates');

const FILTER_KEY = 'eds.templates.filter';
const TYPE_KEY   = 'eds.templates.type';
const INTERNAL_CODE_RE = /^[A-Z][A-Z0-9_.]*$/;

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',      header: 'Code',         defaultWidth: 200 },
  { key: 'template_type_code', header: 'Type',         defaultWidth: 120 },
  { key: 'from_name_default',  header: 'From Name',    defaultWidth: 160 },
  { key: 'from_email_default', header: 'From Email',   defaultWidth: 200 },
  { key: 'is_active',          header: 'Active',       defaultWidth: 80  },
  { key: 'updated_at',         header: 'Updated',      defaultWidth: 110 },
];

function emptyTl(language_id: string): EdsTemplateTlForm {
  return { language_id, subject_line: '', preheader_text: '', body_html: '', body_plain: '' };
}

const TEMPLATE_INFO_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code',      type: 'text',    label: 'Internal Code', required: true, col_span: 1 },
  { key: 'template_type_code', type: 'select',  label: 'Type',          col_span: 1 },
  { key: 'from_name_default',  type: 'text',    label: 'From Name',     required: true, col_span: 1 },
  { key: 'from_email_default', type: 'text',    label: 'From Email',    required: true, col_span: 1 },
  { key: 'reply_to_email',     type: 'text',    label: 'Reply-To',      col_span: 2 },
  { key: 'is_active',          type: 'boolean', label: 'Active',        col_span: 2 },
];
const TEMPLATE_INFO_CORE_LAYOUT = toUldCoreLayout(TEMPLATE_INFO_FIELD_DEFS);

export interface EdsTemplateListPageProps { session: AuthSession; }

export function EdsTemplateListPage({ session }: EdsTemplateListPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <EdsTemplateListPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EdsTemplateListPageInner({ session }: EdsTemplateListPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]             = useState<EdsEmailTemplateRow[]>([]);
  const [filter, setFilter]         = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets (type, active).
  const [facets, setFacets]         = useState<{ type: string[]; active: string[] }>(() => {
    const saved = sessionStorage.getItem(TYPE_KEY);
    return { type: saved ? [saved] : [], active: ['Y'] };
  });
  const [templateTypes, setTemplateTypes] = useState<EdsTemplateTypeRow[]>([]);
  // §17.B2: template_type_code → localized display_text from eds_template_type_tl.
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  // §17.B13 relation: campaigns that use each template (template_id → campaigns).
  const [campaignsByTemplate, setCampaignsByTemplate] = useState<Map<string, { campaign_id: string; code: string }[]>>(new Map());
  const [editing, setEditing]       = useState<EdsEmailTemplateRow | null>(null);
  const [creating, setCreating]     = useState(false);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EdsEmailTemplateRow | null>(null);
  const sort = useSort<EdsTemplateSortKey>('eds.templates.sort', { key: 'internal_code', dir: 'asc' });
  const udcGrid = useUdcGrid('eds_email_template', PAGE_CODE, { pkColumn: 'template_id', rows: rows as unknown as Record<string, unknown>[] });
  // §17.B13: related-children expand/contract (keyed by template_id). Two relations:
  // the campaigns that USE this template (its dependents) and the template's type.
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (id: string): RelatedChildGroup[] => {
    const row = rows.find(r => r.template_id === id);
    if (!row) return [];
    return [
      {
        key: 'campaigns',
        table: 'eds_campaign', pkColumn: 'campaign_id',
        title: t('eds.templates.related.campaigns', 'Campaigns using this template'),
        items: (campaignsByTemplate.get(id) ?? []).map(c => ({ id: c.campaign_id, label: c.code, title: c.code })),
      },
      {
        key: 'type',
        title: t('eds.templates.related.type', 'Type'),
        items: row.template_type_code
          ? [{ id: row.template_type_code, label: typeLabels[row.template_type_code] ?? row.template_type_code, title: row.template_type_code }]
          : [],
      },
    ];
  };
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);
  useEffect(() => { sessionStorage.setItem(TYPE_KEY, facets.type[0] ?? ''); }, [facets.type]);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_email_template')
        .select('*')
        .order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as EdsEmailTemplateRow[]);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    void reload();
    // §17.B13: campaigns per template (RLS-respecting; the caller's JWT applies).
    void apiDb.from('eds_campaign').select('campaign_id, internal_code, template_id')
      .then(({ data }) => {
        const map = new Map<string, { campaign_id: string; code: string }[]>();
        for (const c of (data ?? []) as { campaign_id: string; internal_code: string; template_id: string }[]) {
          const arr = map.get(c.template_id) ?? [];
          arr.push({ campaign_id: c.campaign_id, code: c.internal_code });
          map.set(c.template_id, arr);
        }
        setCampaignsByTemplate(map);
      });
    void apiDb.from('eds_template_type').select('*').order('sort_order')
      .then(({ data }) => setTemplateTypes((data ?? []) as EdsTemplateTypeRow[]));
    // §17.B2: resolve each template_type_code to its localized name.
    const locale = activeLocale;
    void apiDb.from('eds_template_type_tl').select('template_type_code, language_id, display_text')
      .then(({ data }) => {
        type TypeTl = { template_type_code: string; language_id: string; display_text: string };
        const byCode: Record<string, TypeTl[]> = {};
        for (const r of (data ?? []) as TypeTl[]) (byCode[r.template_type_code] ??= []).push(r);
        const labels: Record<string, string> = {};
        for (const [code, tls] of Object.entries(byCode)) {
          const exact = tls.find(t => t.language_id === locale);
          const fb = tls.find(t => t.language_id === 'EN_US') ?? tls[0];
          labels[code] = exact?.display_text ?? fb?.display_text ?? code;
        }
        setTypeLabels(labels);
      });
  }, [reload, activeLocale]);

  const handleDelete = async (r: EdsEmailTemplateRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'eds_email_template', 'template_id', r.template_id);
      if (e) throw e;
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); setDeleteTarget(null); }
  };

  // §17.B5: distinct facet options derived from loaded rows + the type lookup.
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      type: uniq(rows.map(r => r.template_type_code)).map(v => ({ value: v, label: typeLabels[v] ?? v })),
      active: [{ value: 'Y', label: t('common.boolean.active') }, { value: 'N', label: t('common.boolean.inactive') }],
    };
  }, [rows, typeLabels, t]);

  const facetsActive = facets.type.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => {
    setFilter('');
    setFacets({ type: [], active: [] });
  };

  const filtered = useMemo(() => {
    const base = rows.filter(r => {
      const matchText = !filter.trim() ||
        r.internal_code.toLowerCase().includes(filter.toLowerCase()) ||
        r.from_name_default.toLowerCase().includes(filter.toLowerCase()) ||
        r.from_email_default.toLowerCase().includes(filter.toLowerCase());
      // OR within a facet, AND across facets.
      const matchFacets =
        (facets.type.length   === 0 || facets.type.includes(r.template_type_code)) &&
        (facets.active.length === 0 || facets.active.includes(r.is_active));
      return matchText && matchFacets && udcGrid.matchRow(r as unknown as Record<string, unknown>);
    });
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      return compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? ''));
    });
  }, [rows, filter, facets, sort, udcGrid]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('eds.templates.title', 'Email Templates')}
          description={t('eds.templates.list.description', 'Reusable email templates with per-language subject lines and body content.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('eds.templates.new_template')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('eds.templates.load_failed', "Couldn't load templates.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                     placeholder={t('eds.templates.search_placeholder', 'Search templates…')}
                     className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('eds.templates.columns.type', 'Type')}     options={facetOptions.type}   selected={facets.type}   onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('eds.templates.columns.active', 'Active')}  options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy  && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('eds.templates.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related_lbl', 'Related records')} />
                  {visibleCols.map(col => {
                    if (col.key === 'updated_at') {
                      return (
                        <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                            style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>
                          {col.header}
                        </th>
                      );
                    }
                    return (
                      <SortableHeader<EdsTemplateSortKey> key={col.key}
                        sortKey={col.key as EdsTemplateSortKey} label={col.header}
                        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                        style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                    );
                  })}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.template_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.template_id)}
                        hasChildren={hasRelated(r.template_id)}
                        onToggle={() => detail.toggle(r.template_id)}
                        expandLabel={t('common.related_expand', 'Show related records')}
                        collapseLabel={t('common.related_collapse', 'Hide related')}
                        tooltip={t('common.related_lbl', 'Related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code'      && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'template_type_code' && (
                          <span className="rounded bg-csm-info-soft px-1.5 py-0.5 text-csm-xs font-medium text-csm-info border border-csm-info-border">{typeLabels[r.template_type_code] ?? r.template_type_code}</span>
                        )}
                        {col.key === 'from_name_default'  && r.from_name_default}
                        {col.key === 'from_email_default' && <span className="font-mono text-csm-sm text-csm-ink-soft">{r.from_email_default}</span>}
                        {col.key === 'is_active'          && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {col.key === 'updated_at'         && <span className="text-csm-sm text-csm-ink-soft">{formatDate(r.updated_at, activeLocale)}</span>}
                        {udcGrid.isUdcKey(col.key)        && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }}
                              className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('common.edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }}
                              className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('common.delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.template_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.template_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('eds.templates.no_results')} action={
                      canAdd ? <button onClick={() => setCreating(true)}
                              className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                        + {t('eds.templates.new_template')}
                      </button> : undefined
                    } />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <TemplateModal
          session={session}
          template={editing}
          templateTypes={templateTypes}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('eds.templates.delete_confirm_title', 'Delete Template?')}
          message={t('eds.templates.delete_confirm_message', 'Delete template "{{code}}"? Active campaigns referencing it will be affected.', { code: deleteTarget.internal_code })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// EDIT MODAL
// ===========================================================================

type DrawerTab = 'info' | 'content';

interface TemplateModalProps {
  session: AuthSession;
  template: EdsEmailTemplateRow | null;
  templateTypes: EdsTemplateTypeRow[];
  onClose: () => void;
  onSaved: () => void;
}

function TemplateModal({ session, template, templateTypes, onClose, onSaved }: TemplateModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = template === null;
  const [activeTab, setActiveTab] = useState<DrawerTab>('info');
  const [showDiscard, setShowDiscard] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const buildInitial = () => ({
    internalCode: template?.internal_code ?? '',
    templateType: template?.template_type_code ?? (templateTypes[0]?.template_type_code ?? 'transactional'),
    fromName: template?.from_name_default ?? '',
    fromEmail: template?.from_email_default ?? '',
    replyTo: template?.reply_to_email ?? '',
    isActive: template ? template.is_active === 'Y' : true,
    // Seeded from whatever rows the record actually has (empty for a new template) — NOT
    // from useLanguageOptions(). The Content tab (below) derives its per-language
    // tabs/rows from the live active-language list at render time, so this record-identity
    // effect never needs to depend on (or re-run for) that async/live list.
    translations: [] as EdsTemplateTlForm[],
  });

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(buildInitial());
  // Custom fields placed on this page's form become first-class FieldRenderer items:
  // they interleave with native fields and inherit the same personalization.
  const udcForm = useUdcFormFields('eds_email_template', PAGE_CODE, { pkColumn: 'template_id', pk: template?.template_id });
  const infoCoreLayout = useMemo(() => [...TEMPLATE_INFO_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const infoFieldDefs = useMemo(() => [...TEMPLATE_INFO_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout: infoLayout } = useUldLayout('EDS.TEMPLATE.EDIT.MODAL', infoCoreLayout);

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keyed on record identity ONLY (pre-dynamic-locale behavior). Must NEVER re-run when
  // the active-language list resolves or changes — that discarded unsaved edits across
  // this whole form (regression fixed 2026-07-20). The Content tab reads
  // useLanguageOptions() itself, live, at render time.
  useEffect(() => {
    reset(buildInitial());
    setCodeError(null);
    if (!template) return;
    void apiDb.from('eds_email_template_tl').select('*').eq('template_id', template.template_id)
      .then(({ data }) => {
        if (!data) return;
        // Rows come straight from whatever the record has in the DB — not padded/filtered
        // to the active-language list. The Content tab pads missing active locales with an
        // empty row at render time.
        const translations = (data as EdsEmailTemplateTlRow[]).map(ex => ({
          language_id: ex.language_id,
          subject_line: ex.subject_line,
          preheader_text: ex.preheader_text ?? '',
          body_html: ex.body_html,
          body_plain: ex.body_plain ?? '',
        }));
        reset({ ...buildInitial(), translations });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!form.fromName.trim()) throw new Error(t('eds.templates.drawer.errors.from_name_required', 'From Name is required.'));
      if (!form.fromEmail.trim()) throw new Error(t('eds.templates.drawer.errors.from_email_required', 'From Email is required.'));
      let templateId = template?.template_id ?? '';
      if (isCreate) {
        if (!form.internalCode.trim()) throw new Error(t('eds.templates.drawer.errors.internal_code_required', 'Internal code is required.'));
        const { row_id, error: e } = await routedInsert(session, 'eds_email_template', {
          internal_code: form.internalCode.trim().toUpperCase(),
          template_type_code: form.templateType,
          from_name_default: form.fromName.trim(),
          from_email_default: form.fromEmail.trim(),
          reply_to_email: form.replyTo.trim() || null,
          is_active: form.isActive ? 'Y' : 'N',
        });
        if (e) throw e;
        templateId = String(row_id);
      } else {
        const { error: e } = await routedUpdate(session, 'eds_email_template', 'template_id', templateId, {
          template_type_code: form.templateType,
          from_name_default: form.fromName.trim(),
          from_email_default: form.fromEmail.trim(),
          reply_to_email: form.replyTo.trim() || null,
          is_active: form.isActive ? 'Y' : 'N',
        });
        if (e) throw e;
      }
      for (const tl of form.translations) {
        if (!tl.subject_line.trim() && !tl.body_html.trim()) continue;
        // FLAGGED: eds_email_template_tl has a composite PK (template_id, language_id); the routed
        // helpers take a single PK column. Left as a direct upsert pending a composite-key route.
        const { error: e } = await apiDb.from('eds_email_template_tl').upsert({
          template_id: templateId,
          language_id: tl.language_id,
          subject_line: tl.subject_line,
          preheader_text: tl.preheader_text || null,
          body_html: tl.body_html,
          body_plain: tl.body_plain || null,
        }, { onConflict: 'template_id,language_id' });
        if (e) throw e;
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const typeOptions = templateTypes.map(t => ({ internal_code: t.template_type_code, display_text: t.template_type_code }));

  const TABS: { id: string; label: string }[] = [
    { id: 'info',    label: t('eds.templates.drawer.tab_info') },
    { id: 'content', label: t('eds.templates.drawer.tab_content') },
  ];

  const modalTitle = isCreate
    ? t('eds.templates.drawer.new_title')
    : t('eds.templates.drawer.edit_title', { code: template?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="3xl"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as DrawerTab)}
        headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {isPersonalizing && (
          <div className="-mt-4 mb-3 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
            {t('eds.templates.drawer.personalization_banner', 'Toggle fields — hide or mark required. Click icon to save.')}
          </div>
        )}

        {activeTab === 'info' && (
          <FieldRenderer
            fields={infoFieldDefs}
            layout={infoLayout}
            columns={2}
            pageCode="EDS.TEMPLATE.EDIT.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'internal_code') return (
                <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('eds.templates.drawer.fields.internal_code')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.templates.drawer.fields.internal_code')}</label>
                  <input type="text" value={form.internalCode} disabled={!isCreate}
                         onChange={e => setForm(f => ({ ...f, internalCode: e.target.value.toUpperCase() }))}
                         onBlur={e => {
                           const v = e.target.value;
                           if (v && !INTERNAL_CODE_RE.test(v)) setCodeError(t('eds.templates.drawer.errors.invalid_code', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                           else setCodeError(null);
                         }}
                         placeholder="EDS.TMPL.WELCOME"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {codeError && <p className="text-csm-xs text-red-600 mt-1">{codeError}</p>}
                </PersonalizedField>
              );
              if (f.key === 'template_type_code') return (
                <PersonalizedField componentKey={CK} fieldKey="template_type_code" label={t('eds.templates.drawer.fields.template_type_code')}>
                  <FilteredModalSingleSelect label={t('eds.templates.drawer.fields.template_type_code')} options={typeOptions}
                    selectedCode={form.templateType} onChange={c => { if (c) setForm(f => ({ ...f, templateType: c })); }} allowClear={false} />
                </PersonalizedField>
              );
              if (f.key === 'from_name_default') return (
                <PersonalizedField componentKey={CK} fieldKey="from_name_default" label={t('eds.templates.drawer.fields.from_name_default')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.templates.drawer.fields.from_name_default')} <span className="text-csm-danger">*</span></label>
                  <input type="text" value={form.fromName} onChange={e => setForm(f => ({ ...f, fromName: e.target.value }))}
                         placeholder={t('eds.templates.drawer.placeholders.from_name', 'Integrytas Notifications')}
                         className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
                </PersonalizedField>
              );
              if (f.key === 'from_email_default') return (
                <PersonalizedField componentKey={CK} fieldKey="from_email_default" label={t('eds.templates.drawer.fields.from_email_default')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.templates.drawer.fields.from_email_default')} <span className="text-csm-danger">*</span></label>
                  <input type="email" value={form.fromEmail} onChange={e => setForm(f => ({ ...f, fromEmail: e.target.value }))}
                         placeholder={t('eds.templates.drawer.placeholders.from_email', 'no-reply@yourdomain.com')}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
                </PersonalizedField>
              );
              if (f.key === 'reply_to_email') return (
                <PersonalizedField componentKey={CK} fieldKey="reply_to_email" label={t('eds.templates.drawer.fields.reply_to_default')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.templates.drawer.fields.reply_to_default')}</label>
                  <input type="email" value={form.replyTo} onChange={e => setForm(f => ({ ...f, replyTo: e.target.value }))}
                         placeholder={t('eds.templates.drawer.placeholders.reply_to', 'support@yourdomain.com (optional)')}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
                </PersonalizedField>
              );
              if (f.key === 'is_active') return (
                <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('eds.templates.drawer.fields.is_active')}>
                  <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
                    <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 accent-primary" />
                    {t('eds.templates.drawer.fields.is_active')}
                  </label>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey={CK} fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        )}

        {activeTab === 'content' && (
          <ContentTab
            translations={form.translations}
            setTranslations={translations => setForm(f => ({ ...f, translations }))}
          />
        )}

        {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1.5 text-csm-sm text-csm-danger">{error}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_changes_title', 'Discard changes?')}
          message={t('common.discard_changes_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('common.discard', 'Discard')}
          cancelLabel={t('common.keep_editing', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Content Tab (per-language email body)
// ---------------------------------------------------------------------------

interface ContentTabProps {
  translations: EdsTemplateTlForm[];
  setTranslations: (t: EdsTemplateTlForm[]) => void;
}

function ContentTab({ translations, setTranslations }: ContentTabProps) {
  const { t } = useTranslation();
  const { options: languageOptions } = useLanguageOptions();
  const [locale, setLocale]       = useState<string>('');
  useEffect(() => {
    if (!locale && languageOptions.length > 0) setLocale(languageOptions[0].code);
  }, [locale, languageOptions]);
  const [bodyMode, setBodyMode]   = useState<'html' | 'plain'>('html');
  const tl = translations.find(t => t.language_id === locale) ?? emptyTl(locale);

  const update = (patch: Partial<EdsTemplateTlForm>) => {
    setTranslations(
      translations.some(t => t.language_id === locale)
        ? translations.map(t => t.language_id === locale ? { ...t, ...patch } : t)
        // No row yet for this (possibly newly-active) locale — append one instead of
        // silently dropping the edit; leaves every other row/field untouched.
        : [...translations, { ...emptyTl(locale), ...patch }]
    );
  };

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        {languageOptions.map(o => (
          <button key={o.code} onClick={() => setLocale(o.code)}
                  className={`rounded border px-3 py-0.5 text-csm-xs font-medium
                    ${locale === o.code ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}`}>
            {o.name}
          </button>
        ))}
        <div className="ml-4 flex gap-1">
          {(['html', 'plain'] as const).map(m => (
            <button key={m} onClick={() => setBodyMode(m)}
                    className={`rounded border px-2 py-0.5 text-csm-xs uppercase
                      ${bodyMode === m ? 'border-csm-ink-soft bg-csm-ink-soft text-white' : 'border-csm-border bg-white text-csm-muted hover:bg-csm-surface-alt'}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.templates.drawer.fields.subject_line')} <span className="text-csm-danger">*</span></label>
          <input type="text" value={tl.subject_line} onChange={e => update({ subject_line: e.target.value })}
                 placeholder={t('eds.templates.content.subject_placeholder', 'Your subject line here…')}
                 className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('eds.templates.drawer.fields.preheader_text')}</label>
          <input type="text" value={tl.preheader_text} onChange={e => update({ preheader_text: e.target.value })}
                 placeholder={t('eds.templates.content.preheader_placeholder', 'Preview text shown in inbox before opening…')}
                 className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-csm-sm font-medium text-csm-ink-soft">
              {bodyMode === 'html' ? t('eds.templates.content.html_body', 'HTML Body') : t('eds.templates.content.plain_body', 'Plain-Text Body')}
              {bodyMode === 'html' && <span className="ml-1 text-csm-danger">*</span>}
            </label>
            <span className="text-csm-2xs text-csm-muted">
              {bodyMode === 'html' ? t('eds.templates.content.html_hint', 'Full HTML. Use {{tag}} for merge tags.', { tag: '{{variable}}', interpolation: { escapeValue: false } }) : t('eds.templates.content.plain_hint', 'Required by CAN-SPAM as fallback.')}
            </span>
          </div>
          <textarea
            rows={16}
            value={bodyMode === 'html' ? tl.body_html : tl.body_plain}
            onChange={e => update(bodyMode === 'html' ? { body_html: e.target.value } : { body_plain: e.target.value })}
            className="w-full rounded border border-csm-border px-2 py-1.5 font-mono text-csm-xs leading-relaxed focus:border-primary focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}
